import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import Card from '../UI/Card';
import FormField from '../UI/FormField';
import React, { useEffect, useState } from 'react';
import ErrorMessage from '@/components/common/ErrorMessage';
import { errorTypes } from '@/utility/validation';
import { ThrowDateTimeError } from '@/utility/helper';
import { ICategory } from '@/interfaces/category.interface';

interface DiscountProp {
  register: UseFormRegister<ICategory>;
  watch: UseFormWatch<ICategory>;
  setValue: UseFormSetValue<ICategory>;
  control: Control<ICategory>;
  error: FieldErrors<ICategory>;
}

const CategoryDiscount: React.FC<DiscountProp> = ({
  register,
  watch,
  setValue,
  control,
  error,
}) => {
  const prefix = 'activeDiscount.';
  const discountStartDate = watch(`${prefix}startDate`);
  const discountEndDate = watch(`${prefix}endDate`);
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (!discountStartDate || !discountEndDate) {
      setDateError(null);
      return;
    }
    const start = new Date(discountStartDate);
    const end = new Date(discountEndDate);
    setDateError(ThrowDateTimeError(start, end));
  }, [discountStartDate, discountEndDate]);

  useEffect(() => {
    const hasDiscount = [discountStartDate, discountEndDate].some(
      (val) => !!val,
    );

    setDiscountEnabled(hasDiscount);
  }, [discountStartDate, discountEndDate]);

  return (
    <>
      {/* Discount */}
      <Card>
        <h2 className="text-lg font-semibold mb-6">Discount</h2>
        <FormField
          label="Add Category Discount"
          type="checkbox"
          checked={discountEnabled}
          onChange={(e) => {
            const checked = (e.target as HTMLInputElement).checked;
            setDiscountEnabled(checked);
            if (!checked) {
              setValue('activeDiscount', null);
            }
          }}
        />
      </Card>
      {discountEnabled && (
        <>
          {/* Discount Value & Eligibility */}
          <div className="grid grid-cols-1  gap-6 mb-6">
            {/* Discount Value */}
            <Card>
              <div className="space-y-6">
                <h2 className="text-lg font-semibold">Discount Value</h2>
                <div>
                  <FormField
                    label="Label"
                    placeholder="EID Special"
                    min={0}
                    {...register(`${prefix}label`, {
                      ...errorTypes.required('Discount Label'),
                    })}
                  />
                  <ErrorMessage
                    message={error?.activeDiscount?.label?.message}
                  />
                </div>
                {/* Discount Type + Value */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <FormField label="Discount Type" value={'percentage'} />
                  <div>
                    <FormField
                      label="Value"
                      type="number"
                      placeholder="20"
                      suffix="%"
                      step="0.01"
                      min={0}
                      {...register(`${prefix}percentage`, {
                        setValueAs: (value) =>
                          value === '' ? null : parseFloat(value),
                        ...errorTypes.required('Discount value'),
                      })}
                    />
                    <ErrorMessage
                      message={error?.activeDiscount?.percentage?.message}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <Card>
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-4">Active dates</h2>
              <Controller
                name={`${prefix}startDate`}
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-2 gap-2">
                    <FormField
                      label="Start Date"
                      type="date"
                      value={field.value ? field.value.split('T')[0] : ''}
                      onChange={(e) => {
                        const timePart = field.value?.split('T')[1] || '00:00';
                        field.onChange(`${e.target.value}T${timePart}`);
                      }}
                      required
                      className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                    />
                    <FormField
                      label="Start Time"
                      type="time"
                      value={
                        field.value ? field.value.split('T')[1].slice(0, 5) : ''
                      }
                      onChange={(e) => {
                        const datePart =
                          field.value?.split('T')[0] ||
                          new Date().toISOString().split('T')[0];
                        field.onChange(`${datePart}T${e.target.value}`);
                      }}
                      className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                    />
                  </div>
                )}
              />
              <Controller
                name={`${prefix}endDate`}
                control={control}
                render={({ field }) => (
                  <>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {/* Date */}
                      <FormField
                        label="End Date"
                        type="date"
                        value={field.value ? field.value.split('T')[0] : ''}
                        onChange={(e) => {
                          const timePart =
                            field.value?.split('T')[1] || '00:00';
                          field.onChange(`${e.target.value}T${timePart}`);
                        }}
                        className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                      />
                      {/* Time */}
                      <FormField
                        label="End Time"
                        type="time"
                        value={
                          field.value
                            ? field.value.split('T')[1].slice(0, 5)
                            : ''
                        }
                        onChange={(e) => {
                          const datePart =
                            field.value?.split('T')[0] ||
                            new Date().toISOString().split('T')[0];
                          field.onChange(`${datePart}T${e.target.value}`);
                        }}
                        className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                      />
                    </div>
                  </>
                )}
              />
              <ErrorMessage message={dateError as string} />
            </div>
          </Card>
        </>
      )}
    </>
  );
};

export default CategoryDiscount;
